import type {
  WorkerCurrentJob,
  WorkerDiskUsage,
  WorkerHeartbeat,
  WorkerLoadAvg,
  WorkerScratchEntry,
} from "@/api";
import { TableRow } from "@carbon6/c6-frontend-design";

export type WorkerStatusRowClickHandler = (row: WorkerStatusTableRow) => void;

// admin worker status 한 행. status blob에서 화면이 쓰는 부분만 평탄화한다. 이유는 둘이다.
//   - 컬럼 key는 TableRow가 아닌 자기 필드여야 한다.
//   - 누락 가능성을 한 곳에서 처리한다.
// 같은 행이 목록 컬럼이 쓰는 필드와 상세가 쓰는 필드(scratch 경로·항목)를 함께 가진다.
// 그래서 상세를 열 때 추가 요청이 없다.
export class WorkerStatusTableRow extends TableRow {
  readonly worker_id: string;
  readonly current_job: WorkerCurrentJob | null;
  readonly disk: WorkerDiskUsage | null;
  readonly loadavg: WorkerLoadAvg | null;
  readonly scratch_dir: string | null;
  readonly scratch_entry_count: number | null;
  readonly scratch_entries: readonly WorkerScratchEntry[];
  readonly last_progress_at: string | null;
  readonly queues: readonly string[] | null;
  // 워커가 선언한 저장 위치. 빈 목록은 "없음"이 아니라 미보고다(서버가 보고하지 않는 워커에 빈 목록을 준다).
  readonly storageBackends: readonly string[];
  readonly reported_at: string;
  readonly age_seconds: number;
  // online 여부. 서버 is_online 을 그대로 쓴다. Status 배지·정렬·Live only 필터·행 dim이 공유한다.
  readonly online: boolean;
  private readonly rowClickHandler: WorkerStatusRowClickHandler | null;

  constructor(worker: WorkerHeartbeat, rowClickHandler: WorkerStatusRowClickHandler | null = null) {
    super(null);
    this.rowClickHandler = rowClickHandler;
    this.worker_id = worker.worker_id;
    this.current_job = worker.status.current_job ?? null;
    this.disk = worker.status.disk ?? null;
    this.loadavg = worker.status.loadavg ?? null;
    this.scratch_dir = worker.status.scratch_dir ?? null;
    this.scratch_entry_count = worker.status.scratch_entry_count ?? null;
    this.scratch_entries = worker.status.scratch_entries ?? [];
    this.last_progress_at = worker.status.last_progress_at ?? null;
    this.queues = worker.status.queues ?? null;
    this.storageBackends = worker.status.storage_backends ?? [];
    this.reported_at = worker.reported_at;
    this.age_seconds = worker.age_seconds;
    this.online = worker.is_online;
  }

  getRowId(): string {
    return this.worker_id;
  }

  // 모든 행을 표시한다. 가시성은 서버 조회가 정한다.
  canShow(): boolean {
    return true;
  }

  // 선택 UI 가 없는 목록이다. 그래서 선택 대상이 아니다.
  canSelect(): boolean {
    return false;
  }

  get isRowClickable(): boolean {
    return this.rowClickHandler !== null;
  }

  onRowClick(): void {
    this.rowClickHandler?.(this);
  }
}

export function createWorkerStatusTableRows(
  workers: readonly WorkerHeartbeat[],
  rowClickHandler: WorkerStatusRowClickHandler | null = null,
): WorkerStatusTableRow[] {
  return workers.map((worker) => new WorkerStatusTableRow(worker, rowClickHandler));
}
